/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { InitialProps } from '@opengeoweb/core';
import { snackbarTypes } from '@opengeoweb/snackbar';
import type { ViewPreset, WorkspaceSupportedComponentTypes } from './types';
import { PresetAction } from './types';
import type {
  ViewTemplatePreset,
  ViewPresetListItem,
} from '../workspace/types';
import { getTemplatePresetTitle } from '../workspaceList/utils';

export const getSnackbarMessage = (
  action: PresetAction,
  title: string,
  is_shared?: boolean,
): snackbarTypes.TranslatableMessage => {
  // Trim and add quotes
  const trimmedTitle = title.trim();
  switch (action) {
    case PresetAction.DELETE:
      return {
        key: 'workspace-viewpreset-success-delete',
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        params: { TITLE: trimmedTitle },
      };

    case PresetAction.SAVE_AS:
      return {
        key: 'workspace-viewpreset-success-save-as',
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        params: { TITLE: trimmedTitle },
      };
    case PresetAction.EDIT:
      return {
        key: 'workspace-viewpreset-success-edit',
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        params: { TITLE: trimmedTitle },
      };
    case PresetAction.RESET:
      return {
        key: 'workspace-viewpreset-success-reset',
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        params: { TITLE: trimmedTitle },
      };
    case PresetAction.SHARE:
      return {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: is_shared
          ? 'workspace-viewpreset-success-unshare'
          : 'workspace-viewpreset-success-share',
        params: {
          TITLE: trimmedTitle,
        },
      };
    default:
      return {
        key: 'workspace-viewpreset-success-save',
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        params: { TITLE: trimmedTitle },
      };
  }
};

export const emptyViewPreset = (
  componentType: WorkspaceSupportedComponentTypes | undefined = 'Map',
): ViewPreset => {
  const actualComponentType = componentType || 'Map';
  const initialPropsByType: Record<
    WorkspaceSupportedComponentTypes,
    InitialProps | undefined
  > = {
    Map: {
      mapPreset: {},
    },
    TimeSeries: {
      plotPreset: {
        mapId: '',
        plots: [],
        parameters: [],
      },
      services: [],
    },
    Soundings: undefined, // TODO: empty soundings preset https://gitlab.com/opengeoweb/opengeoweb/-/issues/6029
    ViewError: undefined,
    ViewLoading: undefined,
    MultiMap: undefined,
    ModelRunInterval: undefined,
    HarmonieTempAndPrecipPreset: undefined,
    TimeSlider: undefined,
    WarningList: undefined,
    MaritimeWarnings: undefined,
    Plugin: undefined,
    WarningsTimeline: undefined,
  };
  return {
    componentType: actualComponentType,
    keywords: '',
    scope: 'user' as const,
    title: '',
    initialProps: initialPropsByType[actualComponentType],
  };
};

export const findViewTemplatePreset = (
  templates: ViewTemplatePreset[] | undefined,
  presetId?: string,
): ViewTemplatePreset | undefined =>
  presetId
    ? (templates ?? []).find((template) => template.id === presetId)
    : undefined;

export const findViewTemplatePresetForComponentType = (
  templates: ViewTemplatePreset[] | undefined,
  componentType?: WorkspaceSupportedComponentTypes,
): ViewTemplatePreset | undefined =>
  componentType
    ? (templates ?? []).find(
        (template) => template.componentType === componentType,
      )
    : undefined;

export const toTranslatedViewTemplatePreset = (
  template: ViewTemplatePreset,
  t?: TFunction,
): ViewTemplatePreset => ({
  ...template,
  title: getTemplatePresetTitle(template, t),
  titleKey: undefined,
});

export const toViewPresetFromTemplate = (
  template: ViewTemplatePreset,
  t?: TFunction,
): ViewPreset => {
  const componentType =
    template.componentType as WorkspaceSupportedComponentTypes;
  const emptyPreset = emptyViewPreset(componentType);

  return {
    ...emptyPreset,
    title: getTemplatePresetTitle(template, t),
    abstract: template.abstract,
    description: template.description,
    initialProps: template.initialProps ?? emptyPreset.initialProps,
  };
};

export const toViewTemplateListItem = (
  template: ViewTemplatePreset,
  t?: TFunction,
): ViewPresetListItem => ({
  id: template.id,
  title: getTemplatePresetTitle(template, t),
  date: '',
  scope: 'system',
  abstract: template.abstract ?? '',
  description: template.description,
  labels: template.labels,
  isViewTemplate: true,
});

export const toViewTemplateListItems = (
  templates: ViewTemplatePreset[] | undefined,
  t?: TFunction,
): ViewPresetListItem[] =>
  (templates ?? []).map((template) => toViewTemplateListItem(template, t));
